import { Info } from "@components";
import {
  CircleCheck,
  FileIcon,
  Image as ImageIcon,
  TriangleAlert,
  Upload,
  X,
} from "@icons";
import classNames from "classnames";
import {
  DragEvent,
  RefObject,
  createRef,
  useEffect,
  useRef,
  useState,
} from "react";

import Label from "../_shared/label";
import { humanizeAccept } from "./humanize-accept";

export type FileUploadStatus = "done" | "uploading" | "error";

export interface DisplayFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string | null;
  progress?: number;
  status: FileUploadStatus;
  error?: string | null;
  code?: string;
  isExisting?: boolean;
}

export interface UploadResult {
  code: string;
}

export type FileUploader = (
  file: File,
  onProgress: (percent: number) => void,
  signal: AbortSignal,
) => Promise<UploadResult>;

interface FileInputProps {
  name: string;
  label?: string;
  info?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  accept?: string;
  value?: FileList | null;
  onChange?: (files: FileList | null) => void;
  inputRef?: RefObject<HTMLInputElement> | null;
  refCallback?: (ref: HTMLInputElement) => void;
  placeholder?: string;
  hint?: string;
  compact?: boolean;
  maxSize?: number;
  maxFiles?: number;
  existingFileUrl?: string | null;
  onDeleteExisting?: () => void;
  uploader?: FileUploader;
  onUploadedChange?: (codes: string[]) => void;
  onUploadingChange?: (uploading: boolean) => void;
}

function formatSize(bytes: number): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(name: string): string {
  const match = (name || "").match(/\.([^.?]+)(\?.*)?$/);
  return match ? match[1].toUpperCase() : "FILE";
}

function isImageFile(name: string, type?: string): boolean {
  if (type && type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|svg|bmp)$/i.test(name || "");
}

let idCounter = 0;
function nextId(): string {
  idCounter += 1;
  return `f${idCounter}`;
}

export function FileInput({
  name,
  label = "",
  info,
  error = "",
  required = false,
  disabled = false,
  multiple = false,
  accept,
  value,
  onChange = () => {},
  inputRef = null,
  refCallback = () => {},
  placeholder,
  hint,
  compact = false,
  maxSize,
  maxFiles,
  existingFileUrl,
  onDeleteExisting = () => {},
  uploader,
  onUploadedChange = () => {},
  onUploadingChange = () => {},
}: FileInputProps) {
  const isUploadMode = typeof uploader === "function";

  const [displayInfo, setDisplayInfo] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [items, setItems] = useState<DisplayFile[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const controllers = useRef<Record<string, AbortController>>({});

  const innerRef = inputRef || createRef<HTMLInputElement>();

  useEffect(() => {
    if (refCallback && innerRef.current) refCallback(innerRef.current);
  }, [innerRef, refCallback]);

  useEffect(() => {
    return () => {
      Object.values(controllers.current).forEach((c) => c.abort());
    };
  }, []);

  useEffect(() => {
    return () => previewUrls.forEach((url) => URL.revokeObjectURL(url));
  }, [previewUrls]);

  useEffect(() => {
    if (isUploadMode) return;

    previewUrls.forEach((url) => URL.revokeObjectURL(url));

    if (value && value.length > 0) {
      const urls: string[] = [];
      for (let i = 0; i < value.length; i++) {
        urls.push(URL.createObjectURL(value[i]));
      }
      setPreviewUrls(urls);
    } else {
      setPreviewUrls([]);
    }
  }, [value, isUploadMode]);

  const displayError = error || internalError;

  const validate = (files: File[]): string => {
    if (maxFiles && files.length > maxFiles) {
      return `Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed.`;
    }
    if (maxSize) {
      const cap = maxSize * 1024 * 1024;
      for (const file of files) {
        if (file.size > cap) {
          return `"${file.name}" exceeds the ${maxSize}MB limit.`;
        }
      }
    }
    return "";
  };

  useEffect(() => {
    if (!isUploadMode) return;
    onUploadedChange(
      items
        .filter((it) => it.status === "done" && it.code)
        .map((it) => it.code as string),
    );
    onUploadingChange(items.some((it) => it.status === "uploading"));
  }, [items, isUploadMode]);

  const startUpload = (item: DisplayFile, file: File) => {
    if (!uploader) return;
    const controller = new AbortController();
    controllers.current[item.id] = controller;

    uploader(
      file,
      (percent) =>
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id ? { ...it, progress: percent } : it,
          ),
        ),
      controller.signal,
    )
      .then((result) => {
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? {
                  ...it,
                  status: "done" as const,
                  progress: 100,
                  code: result.code,
                }
              : it,
          ),
        );
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        const message =
          err instanceof Error && err.message ? err.message : "Upload failed.";
        setItems((prev) =>
          prev.map((it) =>
            it.id === item.id
              ? { ...it, status: "error" as const, error: message }
              : it,
          ),
        );
      })
      .finally(() => {
        delete controllers.current[item.id];
      });
  };

  const addFiles = (fileList: FileList | null) => {
    if (disabled || !fileList || !fileList.length) return;
    const incoming = Array.from(fileList);
    const validationError = validate(incoming);
    if (validationError) {
      setInternalError(validationError);
      return;
    }
    setInternalError("");

    if (isUploadMode) {
      if (!multiple) {
        Object.values(controllers.current).forEach((c) => c.abort());
        controllers.current = {};
      }
      const mapped: DisplayFile[] = incoming.map((file) => ({
        id: nextId(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: isImageFile(file.name, file.type)
          ? URL.createObjectURL(file)
          : null,
        progress: 0,
        status: "uploading",
      }));
      const next = multiple ? [...items, ...mapped] : mapped.slice(0, 1);
      setItems(next);
      mapped.forEach((item, index) => startUpload(item, incoming[index]));
      return;
    }

    onChange(fileList);
  };

  const removeAt = (id: string) => {
    controllers.current[id]?.abort();

    if (isUploadMode) {
      setItems(items.filter((it) => it.id !== id));
      setInternalError("");
      return;
    }

    onChange(null);
    if (innerRef.current) innerRef.current.value = "";
    setInternalError("");
  };

  const openPicker = () => {
    if (!disabled && innerRef.current) innerRef.current.click();
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!disabled) setIsDragOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);
    if (disabled) return;
    addFiles(event.dataTransfer.files);
  };

  const plainItems: DisplayFile[] = [];
  if (!isUploadMode) {
    if (value && value.length > 0) {
      for (let i = 0; i < value.length; i++) {
        plainItems.push({
          id: `v${i}`,
          name: value[i].name,
          size: value[i].size,
          type: value[i].type,
          url: previewUrls[i] ?? null,
          status: "done",
        });
      }
    } else if (existingFileUrl) {
      plainItems.push({
        id: "existing",
        name: existingFileUrl.split("/").pop() || "Current file",
        size: 0,
        type: "",
        url: isImageFile(existingFileUrl) ? existingFileUrl : null,
        status: "done",
        isExisting: true,
      });
    }
  }

  const shownItems = isUploadMode ? items : plainItems;
  const hasItems = shownItems.length > 0;
  const singleFilled = !multiple && hasItems;

  const autoHint =
    hint ||
    [
      accept &&
        humanizeAccept(accept) !== "" &&
        `Accepted: ${humanizeAccept(accept)}`,
      maxSize && `Up to ${maxSize}MB${multiple ? " each" : ""}`,
      maxFiles && multiple && `Max ${maxFiles} files`,
    ]
      .filter(Boolean)
      .join(" · ");

  const title =
    placeholder ||
    (multiple ? "Choose files or drag & drop" : "Choose a file or drag & drop");
  const [titleHead, titleTail] = title.split(" or ");

  const removeItem = (item: DisplayFile) => {
    if (item.isExisting) {
      onDeleteExisting();
      return;
    }
    removeAt(item.id);
  };

  const renderCard = (item: DisplayFile) => {
    const uploading = item.status === "uploading";
    const done = item.status === "done" && !item.error;
    const image = !!item.url && isImageFile(item.name, item.type);

    return (
      <div
        key={item.id}
        className={classNames(
          "flex items-center gap-3 rounded-lg border px-2.5 py-2 bg-white",
          {
            "border-emerald-200 bg-emerald-50": done,
            "border-red-200 bg-red-50": !!item.error,
            "border-gray-200": !done && !item.error,
          },
        )}
      >
        {image ? (
          <img
            src={item.url as string}
            alt={item.name}
            className="w-10 h-10 flex-shrink-0 rounded object-cover border border-gray-200 bg-gray-100"
          />
        ) : (
          <span className="relative w-10 h-10 flex-shrink-0 grid place-items-center rounded bg-primary-50 text-primary-500">
            {isImageFile(item.name, item.type) ? (
              <ImageIcon size={18} />
            ) : (
              <FileIcon size={18} />
            )}
            <span className="absolute bottom-0.5 inset-x-0 text-center text-[8px] font-bold tracking-wide text-primary-600">
              {extensionOf(item.name)}
            </span>
          </span>
        )}

        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="text-sm font-medium text-gray-800 truncate">
            {item.name}
          </span>
          <span className="flex items-center gap-1.5 text-xs text-gray-500">
            {item.error ? (
              <span className="text-red-500">{item.error}</span>
            ) : (
              <>
                {item.size ? formatSize(item.size) : null}
                {uploading && <span>· Uploading… {item.progress ?? 0}%</span>}
              </>
            )}
          </span>
          {uploading && (
            <div className="h-1 rounded-full bg-gray-100 overflow-hidden mt-0.5">
              <div
                className="h-full bg-primary-500 rounded-full transition-[width] duration-200"
                style={{ width: `${item.progress ?? 0}%` }}
              />
            </div>
          )}
        </div>

        {done && (
          <span className="flex text-emerald-500 flex-shrink-0">
            <CircleCheck size={18} />
          </span>
        )}
        {!disabled && (
          <button
            type="button"
            className="inline-flex items-center justify-center w-6 h-6 flex-shrink-0 rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-500 transition-colors"
            aria-label={`Remove ${item.name}`}
            onClick={(e) => {
              e.stopPropagation();
              removeItem(item);
            }}
          >
            <X size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <fieldset className="flex flex-col w-full">
      {label && (
        <Label
          name={name}
          label={label}
          info={info}
          error={displayError}
          required={required}
          setDisplayInfo={setDisplayInfo}
        />
      )}

      <input
        ref={innerRef}
        id={name}
        name={name}
        type="file"
        className="hidden"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => addFiles(e.target.files)}
      />

      {singleFilled ? (
        <div className="mt-1">
          {renderCard(shownItems[0])}
          {!disabled && (
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700"
              onClick={openPicker}
            >
              <Upload size={13} /> Replace file
            </button>
          )}
        </div>
      ) : (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-invalid={!!displayError}
          className={classNames(
            "mt-1 flex items-center justify-center gap-1 rounded-lg border-2 border-dashed text-center cursor-pointer transition-colors",
            compact ? "flex-row px-4 py-3" : "flex-col px-5 py-6",
            {
              "border-gray-300 bg-white hover:bg-gray-50 hover:border-gray-400":
                !isDragOver && !displayError && !disabled,
              "border-primary-400 bg-primary-50": isDragOver && !displayError,
              "border-red-500 bg-red-50": !!displayError,
              "cursor-not-allowed bg-gray-100 opacity-60": disabled,
            },
          )}
          onClick={openPicker}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onKeyDown={(e) => {
            if (!disabled && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              openPicker();
            }
          }}
        >
          <span
            className={classNames(
              "grid place-items-center rounded-full",
              compact ? "w-8 h-8" : "w-11 h-11 mb-1.5",
              displayError
                ? "bg-red-100 text-red-500"
                : "bg-gray-100 text-primary-500",
            )}
          >
            <Upload size={compact ? 16 : 20} />
          </span>
          <span
            className={classNames(
              "text-sm font-medium",
              displayError ? "text-red-700" : "text-gray-800",
            )}
          >
            <span className="text-primary-600 underline underline-offset-2">
              {compact ? "Upload" : titleHead}
            </span>
            {!compact && titleTail ? ` or ${titleTail}` : ""}
          </span>
          {!compact && (
            <span className="text-xs text-gray-500">
              {multiple ? "Add one or more files" : "Any single file"}
            </span>
          )}
          {!compact && autoHint && (
            <div className="mt-2 text-[11.5px] leading-relaxed text-gray-400">
              {autoHint}
            </div>
          )}
        </div>
      )}

      {multiple && hasItems && (
        <div className="flex flex-col gap-2 mt-2.5">
          {shownItems.map((item) => renderCard(item))}
        </div>
      )}

      {displayError ? (
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-red-500">
          <span className="flex flex-shrink-0">
            <TriangleAlert size={14} />
          </span>
          {displayError}
        </div>
      ) : (
        compact &&
        autoHint && (
          <div className="mt-1.5 text-xs text-gray-500">{autoHint}</div>
        )
      )}

      <Info info={info} displayInfo={displayInfo} />
    </fieldset>
  );
}

export default FileInput;
